// src/hooks/useV2PropertySelection.ts
//
// The property a v2 screen reads. Every v2 read endpoint takes exactly one
// `property`, and it must be a v2 property id from `GET /api/v2/ui/properties`
// — a v1 hotel id is a different id space and is rejected. One cached list is
// shared by every v2 screen; each screen remembers its own last choice.
//
// The remembered id is used only while it is still in the list (it may be a
// v1 hotel id saved by an earlier version, or a property the user lost access
// to); otherwise the first property by display name is selected — the list is
// offered in that order too, never in the API's. An empty list stays empty:
// there is no fallback to the v1 hotels.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useViewSwitch } from '@/providers/preferencesProvider';
import type { V2Property } from '@/types/analyticsV2';
import { withCheckinData, withCheckinOrSurveyData } from '@/lib/v2Properties';
import { isDocumentationOnly } from '@/features/developers/access';

/** Which of the user's v2 properties a screen offers. */
export type V2PropertyFilter = (properties: readonly V2Property[]) => V2Property[];

export type V2PropertyStatus = 'loading' | 'error' | 'empty' | 'ready';

export interface V2PropertySelection {
  status: V2PropertyStatus;
  properties: V2Property[];
  /** The property to read; '' unless `status` is 'ready'. */
  propertyId: string;
  setPropertyId: (id: string) => void;
  retry: () => void;
}

/** Cache key of the shared v2 property list; cleared when the session's user changes. */
export const V2_PROPERTIES_QUERY_KEY = ['insight-v2', 'properties'] as const;

/**
 * Shared list of the v2 properties the current user may view AND that have
 * check-in data (`hasCheckinData`). A property linked to v2 but with nothing
 * stored (a dispenser-only hotel, say) is left out here, once, so the
 * navigation, the start page and every selector agree: a v2 screen is never
 * offered for a property it would show empty. A response without the flag
 * counts as no data.
 */
export const useV2Properties = (filter: V2PropertyFilter = withCheckinData) => {
  const dataProvider = useDataProvider();
  return useQuery<V2Property[], Error, V2Property[]>({
    queryKey: V2_PROPERTIES_QUERY_KEY,
    queryFn: async ({ signal }) => (await (dataProvider as any).getAnalyticsV2Properties(signal)).data ?? [],
    select: filter,
    staleTime: 5 * 60_000,
    // A documentation-only person reads no data: the API would refuse the request.
    enabled: !isDocumentationOnly(),
  });
};

/**
 * Whether the current user has at least one v2 property with check-in data:
 * the navigation's single source for showing the v2 screens. Read from the same cached list as
 * the screens themselves (no second request). `false` while the list loads
 * and when the request fails, so a v2 entry is never offered on a guess.
 */
export const useHasV2Property = (): boolean => {
  const { data, isSuccess } = useV2Properties();
  return isSuccess && data.length > 0;
};

/**
 * Whether the current user has at least one v2 property with check-in data or
 * survey answers: offers «Satisfacción», whose selector lists exactly those.
 * Same cached list, same «never on a guess» rule as useHasV2Property.
 */
export const useHasSurveyProperty = (): boolean => {
  const { data, isSuccess } = useV2Properties(withCheckinOrSurveyData);
  return isSuccess && data.length > 0;
};

/**
 * @param page the screen's key in the preferences store (`pageViews[page].propertyId`).
 * @param filter which properties the screen offers (default: those with check-in data).
 */
export const useV2PropertySelection = (page: string, filter?: V2PropertyFilter): V2PropertySelection => {
  const [remembered, setPropertyId] = useViewSwitch(page, 'propertyId', '');
  const { data, isPending, isError, refetch } = useV2Properties(filter);
  const properties = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName, 'es', { sensitivity: 'base' })),
    [data],
  );

  const status: V2PropertyStatus = isPending ? 'loading' : isError ? 'error' : properties.length === 0 ? 'empty' : 'ready';
  const propertyId =
    status !== 'ready' ? '' : properties.some((p) => p.id === remembered) ? remembered : properties[0].id;

  return { status, properties, propertyId, setPropertyId, retry: () => void refetch() };
};

export default useV2PropertySelection;
